import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { QuestionnaireType } from "@green0meter/api-client";
import { useNavigate } from "react-router-dom";

import { useCsobQuestionnaireStore } from "../../../../Questionnaires.store";
import ChipWithTooltip from "./components/ChipWithTooltip";
import Actions from "./components/Actions";

import { Table } from "@components/common";
import { Button } from "@components/base";
import { useCreateCsobForm } from "@api";
import { useUserStore } from "@store/user";
import { DEFAULT_DATE_TIME_FORMAT } from "@constants";

type FormRow = {
  id: string;
  entityName: string;
  type: React.ReactNode;
  created: string;
  lastEditedBy: string;
  lastSharedWithCsob: string;
  actions: React.ReactNode;
};

interface FormsTable {}

const FormsTable: FC<FormsTable> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { createCsobForm, createCsobFormEnabled } = useCreateCsobForm(user);
  const { forms } = useCsobQuestionnaireStore();
  const placeholderFormId = "client-form-placeholder";

  const createClientForm = (): void =>
    createCsobForm(
      {
        questionnaireType: QuestionnaireType.Client,
        name: "Client form",
        description: "",
        isGroupAsset: false,
        isHedgingAsset: false,
      },
      {
        onSuccess: (res) => {
          navigate(`/csob/form/${res}`);
        },
      },
    );

  const columns: Table<FormRow>["columns"] = [
    {
      key: "entityName",
      title: t("csob.questionnaires.title"),
      dataKey: "entityName",
      tableCellProps: { sx: { minWidth: 150 } },
    },
    {
      key: "type",
      title: t("csob.questionnaires.type"),
      dataKey: "type",
      tableCellProps: { sx: { minWidth: 100 } },
    },
    {
      key: "created",
      title: t("csob.questionnaires.created"),
      dataKey: "created",
      tableCellProps: { sx: { minWidth: 120 } },
    },
    {
      key: "lastEditedBy",
      title: t("csob.questionnaires.lastEditedBy"),
      dataKey: "lastEditedBy",
      tableCellProps: { sx: { minWidth: 180 } },
    },
    {
      key: "lastSharedWithCsob",
      title: t("csob.questionnaires.lastSharedWithCsob"),
      dataKey: "lastSharedWithCsob",
      tableCellProps: { sx: { minWidth: 180 } },
    },
    {
      key: "actions",
      title: t("csob.questionnaires.actions"),
      dataKey: "actions",
      tableCellProps: { sx: { minWidth: 150 } },
    },
  ];

  const rows: FormRow[] = forms
    .filter((f) => (createCsobFormEnabled ? true : f.formId !== placeholderFormId))
    .map((form) => ({
      id: form.formId || "",
      entityName:
        form.questionnaireType === QuestionnaireType.Client
          ? t("csob.questionnaires.clientForm")
          : form.name,
      type: (
        <ChipWithTooltip
          form={form}
          hideTooltip={form.formId === placeholderFormId}
        />
      ),
      created: dayjs(form.createdAtDateTime).format(DEFAULT_DATE_TIME_FORMAT) || "-",
      lastEditedBy: form.lastEdited?.dateTime
        ? `${dayjs(form.lastEdited.dateTime).format(DEFAULT_DATE_TIME_FORMAT)} ${form.lastEdited.user?.firstName} ${form.lastEdited.user?.lastName}`
        : "-",
      lastSharedWithCsob: form.lastSharedWithCsob?.dateTime
        ? dayjs(form.lastSharedWithCsob.dateTime).format(DEFAULT_DATE_TIME_FORMAT)
        : "-",
      actions:
        form.formId === placeholderFormId ? (
          <Button variant="contained" size="small" onClick={createClientForm}>
            {t("csob.questionnaires.startClientForm")}
          </Button>
        ) : (
          <Actions form={form} />
        ),
    }));

  return <Table columns={columns} data={rows} />;
};

export default FormsTable;
